import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer } from "lucide-react";
import { formatDate, formatIDR } from "../lib/format";

// Bulk wage slip printer. Query params:
//   ?ids=id1,id2,id3   (comma-separated wage ids)
//   &layout=2|4|6      (slips per A4)
// Route: /print/bulk-wages
export default function PrintBulkWageSlips() {
  const [search] = useSearchParams();
  const ids = (search.get("ids") || "").split(",").filter(Boolean);
  const layout = parseInt(search.get("layout") || "2", 10);
  const [wages, setWages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get("/job-wages");
        const filtered = data.filter((w) => ids.includes(w.id));
        setWages(filtered);
      } finally { setLoading(false); }
    };
    if (ids.length) fetch(); else setLoading(false);
  }, [search]); // eslint-disable-line

  if (loading) return <div className="p-10">Memuat...</div>;
  if (!wages.length) return <div className="p-10 text-sm text-[#8C9096]">Tidak ada data untuk dicetak.</div>;

  // Layout config: slips per page + grid class
  const layoutConfig = {
    2: { perPage: 2, grid: "grid-cols-1", slipH: "min-h-[126mm]" },
    4: { perPage: 4, grid: "grid-cols-2", slipH: "min-h-[126mm]" },
    6: { perPage: 6, grid: "grid-cols-2", slipH: "min-h-[85mm]" },
  }[layout] || { perPage: 2, grid: "grid-cols-1", slipH: "min-h-[126mm]" };

  // Group wages into pages
  const pages = [];
  for (let i = 0; i < wages.length; i += layoutConfig.perPage) {
    pages.push(wages.slice(i, i + layoutConfig.perPage));
  }

  const printedAt = new Date();

  return (
    <div className="bg-[#EFEBE3] print:bg-white min-h-screen">
      {/* Toolbar (non-print) */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-[#E5E0D8] px-6 py-3 flex items-center justify-between">
        <div className="text-sm">
          <b>{wages.length}</b> slip • layout <b>{layout}/A4</b> • {pages.length} halaman
        </div>
        <div className="flex gap-2 items-center">
          {[2, 4, 6].map((l) => (
            <a key={l} href={`/print/bulk-wages?ids=${ids.join(",")}&layout=${l}`}
              className={`text-xs px-3 py-1.5 rounded border ${layout === l ? "bg-brand text-white border-brand" : "bg-white border-[#E5E0D8] hover:bg-[#FAF6EE]"}`}
              data-testid={`layout-${l}`}>
              {l}/A4
            </a>
          ))}
          <Button onClick={() => window.print()} className="bg-brand text-white" data-testid="btn-do-print-bulk">
            <Printer className="w-4 h-4 mr-1.5" /> Cetak
          </Button>
        </div>
      </div>

      {/* A4 pages */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          .page-break { page-break-after: always; }
          .no-print { display: none; }
        }
        .a4 { width: 210mm; min-height: 297mm; padding: 10mm; margin: 8mm auto; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
        @media print { .a4 { box-shadow: none; margin: 0; padding: 0; } }
        .cut-guide { border: 1px dashed #C4A57B; }
      `}</style>

      {pages.map((pageWages, pi) => (
        <div key={pi} className={`a4 ${pi < pages.length - 1 ? "page-break" : ""}`}>
          <div className={`grid ${layoutConfig.grid} gap-2 h-full`}>
            {pageWages.map((w) => (
              <div key={w.id} className={`cut-guide ${layoutConfig.slipH} p-3 flex flex-col`} data-testid={`slip-${w.id}`}>
                <div className="border-b border-[#C4A57B] pb-1.5 mb-2 flex justify-between items-start">
                  <div>
                    <div className="font-display text-[13px] font-bold leading-tight">DESSY WEDDING GROUP</div>
                    <div className="text-[8px] text-[#5C6066] uppercase tracking-wider">Slip Pembayaran Fee Crew</div>
                  </div>
                  <div className="text-right text-[9px]">
                    <div className="font-mono">{w.payment_batch || "-"}</div>
                    <div className="text-[#8C9096]">{formatDate(w.paid_date || w.job_date)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] mb-2">
                  <div>
                    <div className="text-[8px] uppercase text-[#8C9096]">Nama</div>
                    <div className="font-semibold text-[11px]">{w.staff_name}</div>
                  </div>
                  <div>
                    <div className="text-[8px] uppercase text-[#8C9096]">Peran</div>
                    <div>{w.role}</div>
                  </div>
                  <div>
                    <div className="text-[8px] uppercase text-[#8C9096]">Pemesanan</div>
                    <div className="font-mono text-[9px]">{w.booking_code}</div>
                  </div>
                  <div>
                    <div className="text-[8px] uppercase text-[#8C9096]">Acara</div>
                    <div className="truncate text-[10px]">{w.event_name || "-"}</div>
                  </div>
                  <div>
                    <div className="text-[8px] uppercase text-[#8C9096]">Tgl Job</div>
                    <div>{formatDate(w.job_date)}</div>
                  </div>
                  <div>
                    <div className="text-[8px] uppercase text-[#8C9096]">Metode</div>
                    <div>{w.method || "-"}</div>
                  </div>
                </div>
                <div className="border-t border-[#E5D9C4] pt-1.5 mt-auto">
                  <div className="grid grid-cols-3 gap-1 text-[9px]">
                    <div>
                      <div className="text-[8px] text-[#8C9096]">Fee</div>
                      <div className="font-mono">{formatIDR(w.base_fee)}</div>
                    </div>
                    <div>
                      <div className="text-[8px] text-[#8C9096]">Tambahan</div>
                      <div className="font-mono">{formatIDR(w.additional_fee || 0)}</div>
                    </div>
                    <div>
                      <div className="text-[8px] text-[#8C9096]">Potongan</div>
                      <div className="font-mono">-{formatIDR(w.deduction || 0)}</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-end mt-1.5 pt-1.5 border-t border-[#E5D9C4]">
                    <div className="text-[9px] uppercase text-[#8B5A2A] font-semibold">Total Dibayar</div>
                    <div className="font-display text-[14px] font-bold">{formatIDR(w.final_amount)}</div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 pt-3 text-[8px]">
                    <div className="border-t border-[#8C9096] pt-1 text-center text-[#8C9096]">Yang menerima</div>
                    <div className="border-t border-[#8C9096] pt-1 text-center text-[#8C9096]">Yang membayar</div>
                  </div>
                </div>
              </div>
            ))}
            {/* Fill empty slots to maintain grid */}
            {Array.from({ length: layoutConfig.perPage - pageWages.length }).map((_, i) => (
              <div key={`empty-${i}`} className={layoutConfig.slipH} />
            ))}
          </div>
          <div className="text-[8px] text-[#8C9096] mt-2 text-center print:absolute print:bottom-2 print:left-0 print:right-0">
            Dicetak {printedAt.toLocaleString("id-ID")} • Halaman {pi + 1} dari {pages.length} • Guntinglah sepanjang garis putus
          </div>
        </div>
      ))}
    </div>
  );
}
